import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  Plus,
  Trash2,
  Save
} from 'lucide-react';
import { normalizePokemonData } from '../../utils/pokemonDataUtils';
import { getApiBaseUrl } from '../../utils/apiUrl';

const TeamEditModal = ({ isOpen, onClose, team, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    team_name: '',
    team_name_th: '',
    weight: 1.0,
    recommendation_tier: 'POPULAR',
    is_active: true,
    pokemon: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (team && isOpen) {
      setFormData({
        team_name: team.team_name || '',
        team_name_th: team.team_name_th || '',
        weight: team.weight || 1.0,
        recommendation_tier: team.recommendation_tier || 'POPULAR',
        is_active: team.is_active !== undefined ? team.is_active : true,
        pokemon: (team.pokemon || []).map(poke => ({
          speciesId: poke.speciesId || poke.pokemon_id || poke.id,
          pokemon_id: poke.pokemon_id || poke.speciesId || poke.id,
          fastMove: poke.fastMove || poke.fast_move || '',
          fast_move: poke.fast_move || poke.fastMove || '',
          chargedMoves: [
            poke.charged_move_1 || poke.chargedMoves?.[0] || '',
            poke.charged_move_2 || poke.chargedMoves?.[1] || ''
          ].filter(Boolean),
          charged_move_1: poke.charged_move_1 || poke.chargedMoves?.[0] || '',
          charged_move_2: poke.charged_move_2 || poke.chargedMoves?.[1] || '',
          name: poke.name || poke.Pokemon_Name_EN || poke.pokemon_name || '',
          name_th: poke.name_th || poke.Pokemon_Name_TH || ''
        }))
      });
      setError(null);
      setSuccess(null);
    }
  }, [team, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handlePokemonChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pokemon: prev.pokemon.map((poke, i) => 
        i === index 
          ? { 
              ...poke, 
              [field]: value,
              ...(field === 'fastMove' ? { fast_move: value } : {}),
              ...(field === 'fast_move' ? { fastMove: value } : {}),
              ...(field === 'charged_move_1' ? { chargedMoves: [value, poke.charged_move_2 || poke.chargedMoves?.[1] || ''].filter(Boolean) } : {}),
              ...(field === 'charged_move_2' ? { chargedMoves: [poke.charged_move_1 || poke.chargedMoves?.[0] || '', value].filter(Boolean) } : {})
            }
          : poke
      )
    }));
  };

  const handleAddPokemon = () => {
    if (formData.pokemon.length >= 3) {
      setError('ทีมสามารถมีโปเกมอนได้สูงสุด 3 ตัว');
      return;
    }
    setFormData(prev => ({
      ...prev,
      pokemon: [...prev.pokemon, {
        speciesId: '',
        pokemon_id: '',
        fastMove: '',
        fast_move: '',
        chargedMoves: [],
        charged_move_1: '',
        charged_move_2: '',
        name: '',
        name_th: ''
      }]
    }));
  };

  const handleRemovePokemon = (index) => {
    setFormData(prev => ({
      ...prev,
      pokemon: prev.pokemon.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate
      if (formData.pokemon.length === 0) {
        throw new Error('ทีมต้องมีโปเกมอนอย่างน้อย 1 ตัว');
      }

      if (formData.pokemon.length > 3) {
        throw new Error('ทีมสามารถมีโปเกมอนได้สูงสุด 3 ตัว');
      }

      for (let i = 0; i < formData.pokemon.length; i++) {
        const poke = formData.pokemon[i];
        if (!poke.speciesId && !poke.pokemon_id) {
          throw new Error(`โปเกมอนตัวที่ ${i + 1}: ต้องระบุ speciesId หรือ pokemon_id`);
        }
      }

      const payload = {
        team_name: formData.team_name || null,
        team_name_th: formData.team_name_th || null,
        weight: parseFloat(formData.weight) || 1.0,
        recommendation_tier: formData.recommendation_tier,
        is_active: formData.is_active,
        pokemon: formData.pokemon.map(poke => ({
          speciesId: poke.speciesId || poke.pokemon_id,
          fastMove: poke.fastMove || poke.fast_move || null,
          chargedMoves: [
            poke.charged_move_1 || poke.chargedMoves?.[0] || null,
            poke.charged_move_2 || poke.chargedMoves?.[1] || null
          ].filter(Boolean)
        }))
      };

      const response = await fetch(`${getApiBaseUrl()}/api/teams/${team.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'ไม่สามารถบันทึกข้อมูลได้');
      }

      setSuccess('บันทึกข้อมูลสำเร็จ');
      setTimeout(() => {
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error saving team:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold">แก้ไขทีม</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center space-x-2"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center space-x-2"
              >
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>{success}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Team Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ชื่อทีม (ภาษาอังกฤษ)
              </label>
              <input
                type="text"
                value={formData.team_name}
                onChange={(e) => handleInputChange('team_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Team Name (English)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ชื่อทีม (ภาษาไทย)
              </label>
              <input
                type="text"
                value={formData.team_name_th}
                onChange={(e) => handleInputChange('team_name_th', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ชื่อทีม (ภาษาไทย)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weight (คะแนน)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ประเภททีม
              </label>
              <select
                value={formData.recommendation_tier}
                onChange={(e) => handleInputChange('recommendation_tier', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="POPULAR">ทีมยอดนิยม</option>
                <option value="TOP">Top Teams</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                เปิดใช้งาน
              </label>
            </div>
          </div>

          {/* Pokemon List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">โปเกมอนในทีม ({formData.pokemon.length}/3)</h3>
              {formData.pokemon.length < 3 && (
                <button
                  type="button"
                  onClick={handleAddPokemon}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>เพิ่มโปเกมอน</span>
                </button>
              )}
            </div>

            <div className="space-y-4">
              {formData.pokemon.map((poke, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">โปเกมอน #{index + 1}</h4>
                    {formData.pokemon.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePokemon(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Species ID / Pokemon ID *
                      </label>
                      <input
                        type="text"
                        value={poke.speciesId || poke.pokemon_id || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handlePokemonChange(index, 'speciesId', value);
                          handlePokemonChange(index, 'pokemon_id', value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. metagross, 376"
                        required
                      />
                      {(poke.name || poke.name_th) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {poke.name_th || poke.name || ''}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fast Move
                      </label>
                      <input
                        type="text"
                        value={poke.fastMove || poke.fast_move || ''}
                        onChange={(e) => handlePokemonChange(index, 'fastMove', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. SHADOW_CLAW"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Charged Move 1
                      </label>
                      <input
                        type="text"
                        value={poke.charged_move_1 || poke.chargedMoves?.[0] || ''}
                        onChange={(e) => handlePokemonChange(index, 'charged_move_1', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. METEOR_MASH"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Charged Move 2
                      </label>
                      <input
                        type="text"
                        value={poke.charged_move_2 || poke.chargedMoves?.[1] || ''}
                        onChange={(e) => handlePokemonChange(index, 'charged_move_2', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. EARTHQUAKE (optional)"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังบันทึก...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>บันทึก</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default TeamEditModal;
